import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Skeleton,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import LockResetIcon from "@mui/icons-material/LockReset";
import VerifiedIcon from "@mui/icons-material/Verified";
import PersonRemoveIcon from "@mui/icons-material/PersonRemoveAlt1";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import RemoveModeratorIcon from "@mui/icons-material/RemoveModerator";
import { get, post } from "aws-amplify/api";
import Moment from "moment";
import { useAuth } from "../libs/AuthContext";
import StepUpDialog from "../components/StepUpDialog";

const EMAIL_RE = /^[a-zA-Z0-9.!#$%&’'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;

const ACTION_LABELS = {
  add_manager:    "Added manager",
  remove_manager: "Removed manager",
  promote_admin:  "Promoted to admin",
  demote_admin:   "Revoked admin",
  reset_mfa:      "Reset MFA",
};

const ACTION_COLORS = {
  add_manager:    "success",
  remove_manager: "error",
  promote_admin:  "primary",
  demote_admin:   "warning",
  reset_mfa:      "warning",
};

// Action group → set of underlying audit actions. Lets us collapse
// promote+demote into a single "Admin role" filter button without
// losing per-row colour-coding.
const ACTION_GROUPS = {
  add:    ["add_manager"],
  remove: ["remove_manager"],
  admin:  ["promote_admin", "demote_admin"],
  mfa:    ["reset_mfa"],
};

const RANGE_SECONDS = {
  "24h": 24 * 60 * 60,
  "7d":  7 * 24 * 60 * 60,
  "30d": 30 * 24 * 60 * 60,
};

function ActivityLog({ events, loading, error, nextBefore, onLoadMore, onRefresh, currentEmail }) {
  const [actionFilter, setActionFilter] = useState("all"); // "all" | "add" | "remove" | "admin" | "mfa"
  const [rangeFilter, setRangeFilter] = useState("all");   // "all" | "24h" | "7d" | "30d"
  const [search, setSearch] = useState("");
  const [onlyMine, setOnlyMine] = useState(false);

  const now = Math.floor(Date.now() / 1000);

  // Stats are over the full unfiltered list — gives the "is anything
  // happening" overview regardless of which slice you're looking at.
  const stats = useMemo(() => {
    const last24h = events.filter((e) => now - e.timestamp < RANGE_SECONDS["24h"]).length;
    const last7d = events.filter((e) => now - e.timestamp < RANGE_SECONDS["7d"]).length;
    return { last24h, last7d, total: events.length };
  }, [events, now]);

  const filtered = useMemo(() => {
    const allowed =
      actionFilter === "all" ? null : new Set(ACTION_GROUPS[actionFilter] ?? []);
    const cutoff =
      rangeFilter === "all" ? null : now - RANGE_SECONDS[rangeFilter];
    const needle = search.trim().toLowerCase();
    const me = (currentEmail || "").toLowerCase();
    return events.filter((e) => {
      if (allowed && !allowed.has(e.action)) return false;
      if (cutoff && e.timestamp < cutoff) return false;
      if (onlyMine && me && (e.actorEmail || "").toLowerCase() !== me) return false;
      if (needle) {
        const hay = `${e.actorEmail || ""} ${e.targetEmail || ""}`.toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [events, actionFilter, rangeFilter, search, onlyMine, currentEmail, now]);

  if (error) {
    return <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>;
  }
  if (loading && events.length === 0) {
    return (
      <Card sx={{ overflow: "hidden" }}>
        <Stack spacing={1.5} sx={{ p: 3 }}>
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} variant="text" />
          ))}
        </Stack>
      </Card>
    );
  }

  const filterBar = (
    <Box sx={{ p: 2, borderBottom: 1, borderColor: "divider" }}>
      <Stack spacing={1.5}>
        {/* Stats + refresh */}
        <Stack direction="row" spacing={3} alignItems="center" sx={{ flexWrap: "wrap" }}>
          <Stack direction="row" spacing={2.5} sx={{ flex: 1 }}>
            <Stat label="Last 24h" value={stats.last24h} />
            <Stat label="Last 7d" value={stats.last7d} />
            <Stat label="Total" value={stats.total} />
          </Stack>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={onRefresh} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>

        <Divider />

        {/* Action filter chips */}
        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 0.5 }}>
          <FilterChip label="All" active={actionFilter === "all"} onClick={() => setActionFilter("all")} />
          <FilterChip label="Adds" active={actionFilter === "add"} onClick={() => setActionFilter("add")} color="success" />
          <FilterChip label="Removes" active={actionFilter === "remove"} onClick={() => setActionFilter("remove")} color="error" />
          <FilterChip label="Admin role" active={actionFilter === "admin"} onClick={() => setActionFilter("admin")} color="primary" />
          <FilterChip label="MFA resets" active={actionFilter === "mfa"} onClick={() => setActionFilter("mfa")} color="warning" />
          <Box sx={{ flex: 1 }} />
          <FilterChip label="All time" active={rangeFilter === "all"} onClick={() => setRangeFilter("all")} />
          <FilterChip label="24h" active={rangeFilter === "24h"} onClick={() => setRangeFilter("24h")} />
          <FilterChip label="7d" active={rangeFilter === "7d"} onClick={() => setRangeFilter("7d")} />
          <FilterChip label="30d" active={rangeFilter === "30d"} onClick={() => setRangeFilter("30d")} />
        </Stack>

        {/* Search + only-mine toggle */}
        <Stack direction="row" spacing={1.5} alignItems="center">
          <TextField
            size="small"
            placeholder="Search actor or target email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch("")}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={onlyMine}
                onChange={(e) => setOnlyMine(e.target.checked)}
                disabled={!currentEmail}
              />
            }
            label="Only my actions"
            slotProps={{ typography: { variant: "body2" } }}
          />
        </Stack>
      </Stack>
    </Box>
  );

  return (
    <Card sx={{ overflow: "hidden" }}>
      {filterBar}

      {events.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
          No admin actions recorded yet.
        </Box>
      ) : filtered.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
          <Typography variant="body2">No events match your filters.</Typography>
          <Button
            size="small"
            sx={{ mt: 1 }}
            onClick={() => {
              setActionFilter("all");
              setRangeFilter("all");
              setSearch("");
              setOnlyMine(false);
            }}
          >
            Clear filters
          </Button>
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={120}>Action</TableCell>
                <TableCell>Actor</TableCell>
                <TableCell>Target</TableCell>
                <TableCell>Detail</TableCell>
                <TableCell width={160}>When</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((e) => {
                const label = ACTION_LABELS[e.action] ?? e.action;
                const color = ACTION_COLORS[e.action] ?? "default";
                const detailParts = [];
                if (e.metadata?.makeAdmin) detailParts.push("as admin");
                if (e.metadata?.reAdded) detailParts.push("re-added existing user");
                if (e.metadata?.disabled) detailParts.push("user disabled");
                if (e.action === "demote_admin" || e.action === "promote_admin") {
                  detailParts.push(`admin=${Boolean(e.metadata?.admin)}`);
                }
                return (
                  <TableRow key={e.auditId} hover>
                    <TableCell>
                      <Chip label={label} size="small" color={color} variant="outlined" />
                    </TableCell>
                    <TableCell sx={{ fontSize: "0.875rem" }}>{e.actorEmail || e.actorUserId}</TableCell>
                    <TableCell sx={{ fontSize: "0.875rem" }}>{e.targetEmail || e.targetUserId || "—"}</TableCell>
                    <TableCell sx={{ color: "text.secondary", fontSize: "0.85rem" }}>
                      {detailParts.join(", ") || "—"}
                    </TableCell>
                    <TableCell sx={{ color: "text.secondary" }}>
                      <Tooltip title={Moment.unix(e.timestamp).format("MMM D, YYYY HH:mm:ss")}>
                        <Typography variant="body2">{Moment.unix(e.timestamp).fromNow()}</Typography>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Show counts and load-more only when filters aren't hiding the cursor */}
      {events.length > 0 && filtered.length !== events.length ? (
        <Box sx={{ textAlign: "center", py: 1.5, color: "text.secondary" }}>
          <Typography variant="caption">
            Showing {filtered.length} of {events.length} loaded events
          </Typography>
        </Box>
      ) : null}

      {nextBefore ? (
        <Box sx={{ textAlign: "center", py: 2, borderTop: 1, borderColor: "divider" }}>
          <Button onClick={onLoadMore} disabled={loading} size="small">
            {loading ? "Loading…" : "Load older"}
          </Button>
        </Box>
      ) : null}
    </Card>
  );
}

function Stat({ label, value }) {
  return (
    <Stack direction="row" spacing={0.75} alignItems="baseline">
      <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1 }}>{value}</Typography>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Stack>
  );
}

function FilterChip({ label, active, onClick, color }) {
  return (
    <Chip
      label={label}
      size="small"
      onClick={onClick}
      color={active ? (color || "primary") : "default"}
      variant={active ? "filled" : "outlined"}
      sx={{ cursor: "pointer" }}
    />
  );
}

async function readErrorBody(error) {
  const candidates = [
    error?.response?.body,
    error?.response,
    error?.underlyingError?.response?.body,
    error?.cause?.response?.body,
    error?.body,
  ];
  for (const src of candidates) {
    if (src == null) continue;
    if (typeof src === "object" && (src.error || src.detail || src.reference)) return src;
    if (typeof src?.json === "function") {
      try {
        return await Promise.race([
          src.json(),
          new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), 1000))
        ]);
      } catch (_) { /* */ }
    }
    if (typeof src?.text === "function") {
      try {
        const text = await Promise.race([
          src.text(),
          new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), 1000))
        ]);
        if (typeof text === "string" && text) return JSON.parse(text);
      } catch (_) { /* */ }
    }
    if (typeof src === "string" && src) {
      try { return JSON.parse(src); } catch (_) { /* */ }
    }
  }
  return null;
}

export default function LicenseManagerList() {
  const { emailAddress, isLicenseManagerAdmin } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [managers, setManagers] = useState([]);
  const [error, setError] = useState("");
  const [confirmReset, setConfirmReset] = useState(null); // { user }
  const [confirmRemove, setConfirmRemove] = useState(null); // { user }
  const [confirmAdminToggle, setConfirmAdminToggle] = useState(null); // { user, makeAdmin }
  const [addOpen, setAddOpen] = useState(false);
  const [addEmail, setAddEmail] = useState("");
  const [addMakeAdmin, setAddMakeAdmin] = useState(false);
  const [addError, setAddError] = useState("");
  const [actionInProgress, setActionInProgress] = useState(false);
  const [snackbar, setSnackbar] = useState(null);
  const [pendingRetry, setPendingRetry] = useState(null);
  const [tab, setTab] = useState("managers");
  const [auditEvents, setAuditEvents] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState("");
  const [auditNextBefore, setAuditNextBefore] = useState(null);

  const fetchAudit = async (before = null) => {
    setAuditLoading(true);
    setAuditError("");
    try {
      // Fetch a generous chunk so the client-side filters have material
      // to work with. Audit volume is small (handful per month), so a
      // 200-row pull is comfortably under the lambda's MAX_LIMIT.
      const path = before
        ? `/license-managers/audit?limit=200&before=${before}`
        : "/license-managers/audit?limit=200";
      const op = get({ apiName: "license", path });
      const { body } = await op.response;
      const result = await body.json();
      const events = Array.isArray(result?.events) ? result.events : [];
      setAuditEvents((prev) => (before ? [...prev, ...events] : events));
      setAuditNextBefore(result?.nextBefore ?? null);
    } catch (e) {
      setAuditError(e?.message ?? "Failed to load audit log");
    } finally {
      setAuditLoading(false);
    }
  };

  // Load the audit log lazily the first time the tab is opened.
  useEffect(() => {
    if (tab === "audit" && auditEvents.length === 0 && !auditLoading && !auditError) {
      fetchAudit();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const fetchManagers = async () => {
    setIsLoading(true);
    setError("");
    try {
      const op = get({ apiName: "license", path: "/license-managers" });
      const { body } = await op.response;
      const result = await body.json();
      setManagers(Array.isArray(result?.managers) ? result.managers : []);
    } catch (e) {
      setError(e?.message ?? "Failed to load licence managers");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchManagers();
  }, []);

  // Generic step-up-aware action runner. Side-effects on success
  // (close dialog, refresh list, snackbar) MUST be passed via
  // onSuccess so they survive the step-up retry flow — pendingRetry
  // re-runs runWithStepUp itself, and without onSuccess captured in
  // the closure, the dialog stays open and the list stays stale.
  const runWithStepUp = async (operation, label, onSuccess) => {
    setActionInProgress(true);
    try {
      await operation();
      if (onSuccess) onSuccess();
      return { ok: true };
    } catch (e) {
      const errorBody = await readErrorBody(e);
      if (errorBody?.error === "STEP_UP_REQUIRED") {
        setPendingRetry(() => () => runWithStepUp(operation, label, onSuccess));
        return { ok: false, stepUp: true };
      }
      const code = errorBody?.error;
      let message = errorBody?.detail ?? e?.message ?? `${label} failed`;
      if (code === "USER_ALREADY_EXISTS") {
        message = errorBody?.detail ?? "A user with that email already exists.";
      } else if (code === "INVALID_EMAIL") {
        message = errorBody?.detail ?? "That doesn't look like a valid email address.";
      } else if (code === "USER_NOT_FOUND") {
        message = "User not found.";
      } else if (code === "SELF_REMOVAL_NOT_ALLOWED") {
        message = "You can't remove yourself.";
      } else if (code === "SELF_DEMOTION_NOT_ALLOWED") {
        message = "You can't demote yourself.";
      } else if (code === "SELF_PROMOTION_NOT_ALLOWED") {
        message = "You can't promote yourself.";
      } else if (code === "SELF_RESET_NOT_ALLOWED") {
        message = "You can't reset your own MFA. Use the Set up MFA page if you want to re-enrol.";
      } else if (code === "TARGET_NOT_LICENSE_MANAGER") {
        message = "That user isn't a licence manager.";
      } else if (code === "NOT_LICENSE_MANAGER_ADMIN") {
        message = "Only licence-manager admins can do this.";
      }
      setSnackbar({ severity: "error", message });
      return { ok: false };
    } finally {
      setActionInProgress(false);
    }
  };

  const performReset = (user) =>
    runWithStepUp(
      async () => {
        const op = post({
          apiName: "license",
          path: "/license-managers/reset-mfa",
          options: { body: { username: user.userId } }
        });
        await op.response;
      },
      "MFA reset",
      () => {
        setSnackbar({
          severity: "success",
          message: `MFA reset for ${user.email}. They'll set up again on next edit.`
        });
        setConfirmReset(null);
        fetchManagers();
      }
    );

  const performRemove = (user) =>
    runWithStepUp(
      async () => {
        const op = post({
          apiName: "license",
          path: "/license-managers/remove",
          options: { body: { username: user.userId } }
        });
        await op.response;
      },
      "Remove",
      () => {
        setSnackbar({ severity: "success", message: `Removed ${user.email}.` });
        setConfirmRemove(null);
        fetchManagers();
      }
    );

  const performAdminToggle = (user, makeAdmin) =>
    runWithStepUp(
      async () => {
        const op = post({
          apiName: "license",
          path: "/license-managers/admin",
          options: { body: { username: user.userId, admin: makeAdmin } }
        });
        await op.response;
      },
      makeAdmin ? "Promote" : "Demote",
      () => {
        setSnackbar({
          severity: "success",
          message: makeAdmin
            ? `${user.email} is now a licence-manager admin.`
            : `${user.email}'s admin role revoked.`
        });
        setConfirmAdminToggle(null);
        fetchManagers();
      }
    );

  const performAdd = async () => {
    const email = addEmail.trim().toLowerCase();
    if (!EMAIL_RE.test(email)) {
      setAddError("That doesn't look like a valid email address.");
      return;
    }
    setAddError("");
    return runWithStepUp(
      async () => {
        const op = post({
          apiName: "license",
          path: "/license-managers",
          options: { body: { email, makeAdmin: addMakeAdmin } }
        });
        await op.response;
      },
      "Add manager",
      () => {
        setSnackbar({
          severity: "success",
          message: `Invited ${email}. They have 15 minutes to claim their temp password.`
        });
        setAddOpen(false);
        setAddEmail("");
        setAddMakeAdmin(false);
        fetchManagers();
      }
    );
  };

  const handleAddDialogClose = () => {
    if (actionInProgress) return;
    setAddOpen(false);
    setAddEmail("");
    setAddMakeAdmin(false);
    setAddError("");
  };

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: "-0.02em" }}>
            Licence managers
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {managers.length} {managers.length === 1 ? "user" : "users"} in absolute.license-manager
          </Typography>
        </Box>
        {isLicenseManagerAdmin ? (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddOpen(true)}
          >
            Add manager
          </Button>
        ) : null}
      </Stack>

      {isLicenseManagerAdmin ? (
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
        >
          <Tab value="managers" label="Managers" />
          <Tab value="audit" label="Activity log" />
        </Tabs>
      ) : null}

      {tab === "managers" && error ? (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      ) : null}

      {!isLicenseManagerAdmin ? (
        <Alert severity="info" sx={{ mb: 3, "& .MuiAlert-message": { fontSize: "0.85rem" } }}>
          Add, remove, promote, and reset-MFA actions are restricted to <code>absolute.license-manager-admin</code>. Ask an admin if you need one of those.
        </Alert>
      ) : null}

      {tab === "audit" ? (
        <ActivityLog
          events={auditEvents}
          loading={auditLoading}
          error={auditError}
          nextBefore={auditNextBefore}
          onLoadMore={() => fetchAudit(auditNextBefore)}
          onRefresh={() => fetchAudit()}
          currentEmail={emailAddress}
        />
      ) : null}

      <Card sx={{ overflow: "hidden", display: tab === "managers" ? "block" : "none" }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Email</TableCell>
                <TableCell width={120}>Role</TableCell>
                <TableCell width={140}>MFA</TableCell>
                <TableCell width={140}>Status</TableCell>
                <TableCell>Last seen</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right" width={180}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 3 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={7}><Skeleton variant="text" /></TableCell>
                  </TableRow>
                ))
              ) : managers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
                    No licence managers yet.
                  </TableCell>
                </TableRow>
              ) : (
                managers.map((row) => {
                  const isSelf = row.email && emailAddress && row.email.toLowerCase() === emailAddress.toLowerCase();
                  const isAdmin = Boolean(row.admin);
                  return (
                    <TableRow key={row.userId} hover>
                      <TableCell sx={{ fontWeight: 500 }}>
                        {row.email}
                        {isSelf ? (
                          <Chip label="You" size="small" sx={{ ml: 1, height: 20, fontSize: "0.7rem" }} />
                        ) : null}
                      </TableCell>
                      <TableCell>
                        {isAdmin ? (
                          <Chip
                            icon={<AdminPanelSettingsIcon />}
                            label="Admin"
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ) : (
                          <Chip label="Manager" size="small" variant="outlined" />
                        )}
                      </TableCell>
                      <TableCell>
                        {row.mfaEnabled ? (
                          <Chip
                            icon={<VerifiedIcon />}
                            label="Enrolled"
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        ) : (
                          <Chip label="Not enrolled" size="small" color="warning" />
                        )}
                      </TableCell>
                      <TableCell sx={{ color: "text.secondary" }}>
                        <Typography variant="body2">{row.status ?? "—"}</Typography>
                      </TableCell>
                      <TableCell sx={{ color: "text.secondary" }}>
                        {row.lastSeenAt ? (
                          <Tooltip title={Moment.unix(row.lastSeenAt).format("MMM D, YYYY HH:mm")}>
                            <Typography variant="body2">{Moment.unix(row.lastSeenAt).fromNow()}</Typography>
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.disabled">Never</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ color: "text.secondary" }}>
                        {row.createdDateTime ? Moment(row.createdDateTime).format("MMM D, YYYY") : "—"}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                          {isLicenseManagerAdmin ? (
                            <Tooltip title={
                              isSelf
                                ? "You can't change your own admin role"
                                : isAdmin ? "Revoke admin" : "Make admin"
                            }>
                              <span>
                                <IconButton
                                  size="small"
                                  color={isAdmin ? "default" : "primary"}
                                  disabled={isSelf || actionInProgress}
                                  onClick={() => setConfirmAdminToggle({ user: row, makeAdmin: !isAdmin })}
                                >
                                  {isAdmin ? <RemoveModeratorIcon fontSize="small" /> : <AdminPanelSettingsIcon fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          ) : null}
                          <Tooltip title={
                            !isLicenseManagerAdmin
                              ? "Admins only"
                              : isSelf
                                ? "You can't reset your own MFA"
                                : !row.mfaEnabled
                                  ? "MFA isn't enrolled — nothing to reset"
                                  : "Reset MFA"
                          }>
                            <span>
                              <IconButton
                                size="small"
                                color="warning"
                                disabled={!isLicenseManagerAdmin || isSelf || !row.mfaEnabled || actionInProgress}
                                onClick={() => setConfirmReset({ user: row })}
                              >
                                <LockResetIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          {isLicenseManagerAdmin ? (
                            <Tooltip title={isSelf ? "You can't remove yourself" : "Remove manager"}>
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  disabled={isSelf || actionInProgress}
                                  onClick={() => setConfirmRemove({ user: row })}
                                >
                                  <PersonRemoveIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          ) : null}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <Dialog open={addOpen} onClose={handleAddDialogClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Add a licence manager</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <DialogContentText sx={{ color: "text.primary" }}>
              We'll create a Cognito user, email them a temporary password, and give them 15 minutes to claim it. After that the invite expires and the account is disabled.
            </DialogContentText>
            {addError ? <Alert severity="error">{addError}</Alert> : null}
            <TextField
              value={addEmail}
              onChange={(e) => setAddEmail(e.target.value)}
              label="Email"
              type="email"
              autoFocus
              fullWidth
              disabled={actionInProgress}
              onKeyDown={(e) => {
                if (e.key === "Enter" && addEmail) performAdd();
              }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={addMakeAdmin}
                  onChange={(e) => setAddMakeAdmin(e.target.checked)}
                  disabled={actionInProgress}
                />
              }
              label="Also grant admin (can manage other managers)"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleAddDialogClose} disabled={actionInProgress}>Cancel</Button>
          <Button
            onClick={performAdd}
            variant="contained"
            disabled={actionInProgress || !addEmail}
          >
            {actionInProgress ? "Inviting…" : "Send invite"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(confirmReset)} onClose={() => !actionInProgress && setConfirmReset(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Reset MFA?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "text.primary" }}>
            <strong>{confirmReset?.user.email}</strong> will lose their TOTP enrolment. They'll be walked through fresh setup the next time they try to edit a licence. Any active step-up session they hold will be revoked immediately.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmReset(null)} disabled={actionInProgress}>Cancel</Button>
          <Button
            onClick={() => performReset(confirmReset.user)}
            variant="contained"
            color="warning"
            disabled={actionInProgress}
          >
            {actionInProgress ? "Resetting…" : "Reset MFA"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(confirmRemove)} onClose={() => !actionInProgress && setConfirmRemove(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>Remove licence manager?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "text.primary" }}>
            <strong>{confirmRemove?.user.email}</strong> will be removed from <code>absolute.license-manager</code> (and from <code>absolute.license-manager-admin</code> if applicable). If they have no other Cognito groups, the user will also be disabled. Any active step-up session is revoked immediately.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmRemove(null)} disabled={actionInProgress}>Cancel</Button>
          <Button
            onClick={() => performRemove(confirmRemove.user)}
            variant="contained"
            color="error"
            disabled={actionInProgress}
          >
            {actionInProgress ? "Removing…" : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(confirmAdminToggle)} onClose={() => !actionInProgress && setConfirmAdminToggle(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600 }}>
          {confirmAdminToggle?.makeAdmin ? "Grant admin role?" : "Revoke admin role?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "text.primary" }}>
            {confirmAdminToggle?.makeAdmin ? (
              <>
                <strong>{confirmAdminToggle?.user.email}</strong> will be able to add, remove, promote, and reset MFA on other licence managers.
              </>
            ) : (
              <>
                <strong>{confirmAdminToggle?.user.email}</strong> will lose admin powers (add/remove/promote/MFA-reset). They'll keep their licence-manager role.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmAdminToggle(null)} disabled={actionInProgress}>Cancel</Button>
          <Button
            onClick={() => performAdminToggle(confirmAdminToggle.user, confirmAdminToggle.makeAdmin)}
            variant="contained"
            color={confirmAdminToggle?.makeAdmin ? "primary" : "warning"}
            disabled={actionInProgress}
          >
            {actionInProgress ? "Working…" : (confirmAdminToggle?.makeAdmin ? "Make admin" : "Revoke admin")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {snackbar ? (
          <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar(null)}>
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>

      <StepUpDialog
        open={Boolean(pendingRetry)}
        onClose={() => setPendingRetry(null)}
        onVerified={async () => {
          const fn = pendingRetry;
          setPendingRetry(null);
          if (fn) await fn();
        }}
      />
    </Box>
  );
}
